// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 09-data-brokering.md
import * as React from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  connectionProfileForMode,
  defaultConnectionProfile,
  getCatalogEntry,
  INTEGRATION_AUTH_MODES,
  secretPlaceholderFor,
  type IntegrationAuthMode,
  type IntegrationListItem,
} from '@greenlight/shared';
import {
  composeClientCredentialsSecret,
  composeGithubAppSecret,
  composePrivateKeyJwtSecret,
} from './catalogRegistration';
import { PrivateKeyEntry } from './PrivateKeyEntry';

/**
 * The wire shape of a credential's vaulted secret, derived from the
 * integration's auth mode and (for client credentials) its client-auth method:
 * - `single` — one opaque value (API key, PAT, password, …).
 * - `client_secret_pair` — the composed `{client_id, client_secret}` JSON the
 *   proxy mints client_secret_post/basic tokens from (docs/09 §Tier 2).
 * - `private_key_jwt` — the composed `{client_id, certificate_id, private_key}`
 *   JSON the proxy signs JWT client assertions from (NetSuite-style M2M).
 * - `github_app` — the composed `{app_id, installation_id, private_key}` JSON
 *   the proxy uses to mint installation tokens (docs/09 §Tier 2).
 */
export type SecretShape = 'single' | 'client_secret_pair' | 'private_key_jwt' | 'github_app';

export function secretShapeFor(
  authMode: string | undefined,
  tokenEndpointAuthMethod: IntegrationListItem['token_endpoint_auth_method'] | undefined,
): SecretShape {
  if (authMode !== 'client_credentials') return 'single';
  if (tokenEndpointAuthMethod === 'private_key_jwt') return 'private_key_jwt';
  if (tokenEndpointAuthMethod === 'github_app') return 'github_app';
  return 'client_secret_pair';
}

/**
 * A write-only secret being entered for a credential. `clientId` and
 * `certificateId` apply only to the composed client-credentials shapes —
 * replacing or adding one needs every part, never a lone value.
 */
export interface SecretDraft {
  clientId: string;
  certificateId: string;
  secret: string;
  generated: boolean;
}

export const EMPTY_SECRET_DRAFT: SecretDraft = {
  clientId: '',
  certificateId: '',
  secret: '',
  generated: false,
};

export function isSecretDraftEmpty(draft: SecretDraft): boolean {
  return !draft.clientId.trim() && !draft.certificateId.trim() && !draft.secret;
}

export function clientIdLabelFor(
  catalogKey: string | null | undefined,
  authMode?: string | null,
  mechanism?: unknown,
): string {
  const profile = profileFromSeed(catalogKey, authMode, mechanism);
  return profile?.clientIdLabel ?? 'Client ID';
}

export function clientIdPlaceholderFor(
  catalogKey: string | null | undefined,
  authMode?: string | null,
  mechanism?: unknown,
): string | undefined {
  return profileFromSeed(catalogKey, authMode, mechanism)?.clientIdPlaceholder;
}

export function secretPlaceholderForCatalog(
  catalogKey: string | null | undefined,
  authMode?: string | null,
  mechanism?: unknown,
): string | undefined {
  const profile = profileFromSeed(catalogKey, authMode, mechanism);
  if (!profile) return undefined;
  return secretPlaceholderFor(
    profile.credentialKind,
    profile.secretPlaceholder,
    profile.tokenEndpointAuthMethod,
  );
}

/**
 * The registered integration's catalog profile, by auth mode and (for a
 * user_delegated integration) the mechanism its auth_config names, so an entry
 * with two profiles of one mode resolves the one that was registered.
 */
function profileFromSeed(
  catalogKey: string | null | undefined,
  authMode?: string | null,
  mechanism?: unknown,
): ReturnType<typeof defaultConnectionProfile> | undefined {
  const entry = getCatalogEntry(catalogKey);
  if (!entry) return undefined;
  if (authMode && (INTEGRATION_AUTH_MODES as readonly string[]).includes(authMode)) {
    return connectionProfileForMode(
      entry,
      authMode as IntegrationAuthMode,
      typeof mechanism === 'string' ? mechanism : null,
    );
  }
  return defaultConnectionProfile(entry);
}

/** What the secret input is called in labels and error copy, per shape/mode. */
export function secretNounFor(shape: SecretShape, authMode: string): string {
  if (shape === 'private_key_jwt' || shape === 'github_app') return 'private key';
  if (shape === 'client_secret_pair' || authMode === 'service_principal') return 'client secret';
  if (authMode === 'sql_password') return 'password';
  return 'secret';
}

/**
 * Validate a draft for submission. `required` distinguishes "add" (a value must
 * be entered) from "replace" (blank means keep the stored value). A partially
 * filled composed shape is always an error — storing a subset of the parts
 * would corrupt the vaulted JSON and break token minting.
 */
export function secretDraftError(
  draft: SecretDraft,
  opts: { shape: SecretShape; clientIdLabel: string; secretNoun: string; required: boolean },
): string | null {
  const empty = isSecretDraftEmpty(draft);
  const triple = opts.shape === 'private_key_jwt' || opts.shape === 'github_app';
  const parts = triple
    ? [
        `the ${opts.clientIdLabel}`,
        opts.shape === 'github_app' ? 'the Installation ID' : 'the Certificate ID',
        `the ${opts.secretNoun}`,
      ]
    : opts.shape === 'client_secret_pair'
      ? [`the ${opts.clientIdLabel}`, `the ${opts.secretNoun}`]
      : [`the ${opts.secretNoun}`];
  if (empty) {
    return opts.required ? `Enter ${parts.join(', ').replace(/, ([^,]*)$/, ' and $1')}.` : null;
  }
  const missing = triple
    ? !draft.clientId.trim() || !draft.certificateId.trim() || !draft.secret
    : opts.shape === 'client_secret_pair'
      ? !draft.clientId.trim() || !draft.secret
      : false;
  if (missing) {
    return `Enter ${parts.join(', ').replace(/, ([^,]*)$/, ' and $1')} together — the stored value is the composed set, so a partial entry would break token minting.`;
  }
  if (opts.shape === 'github_app' && !/^\d+$/.test(draft.certificateId.trim())) {
    return 'Enter the numeric installation ID from the installation URL.';
  }
  return null;
}

/** The string the API stores: the composed JSON for client-credentials shapes, the bare secret otherwise. The private key is never trimmed — whitespace inside a PEM is significant. */
export function composeSecretDraft(draft: SecretDraft, shape: SecretShape): string {
  if (shape === 'private_key_jwt')
    return composePrivateKeyJwtSecret(draft.clientId, draft.certificateId, draft.secret);
  if (shape === 'github_app')
    return composeGithubAppSecret(draft.clientId, draft.certificateId, draft.secret);
  if (shape === 'client_secret_pair')
    return composeClientCredentialsSecret(draft.clientId, draft.secret);
  return draft.secret;
}

export interface CredentialSecretInputsProps {
  /** Unique per usage site (and per credential row) — prefixes the input ids. */
  idPrefix: string;
  shape: SecretShape;
  clientIdLabel: string;
  clientIdPlaceholder?: string;
  secretNoun: string;
  draft: SecretDraft;
  onChange: (draft: SecretDraft) => void;
  /** Replacement contexts hint that blank keeps the stored value. */
  replacement?: boolean;
  /** Catalog example for a new secret (ignored when `replacement`). */
  secretExamplePlaceholder?: string;
}

/**
 * The write-only inputs a credential's secret is entered through, shaped by the
 * integration's auth mode: a single field for static-key modes, the client id +
 * client secret pair for client_secret_post/basic, and the client id +
 * certificate id + multiline private key triple for private_key_jwt. Stored
 * values are never shown.
 */
export function CredentialSecretInputs({
  idPrefix,
  shape,
  clientIdLabel,
  clientIdPlaceholder,
  secretNoun,
  draft,
  onChange,
  replacement = false,
  secretExamplePlaceholder,
}: CredentialSecretInputsProps): React.JSX.Element {
  const secretLabel = `${secretNoun.charAt(0).toUpperCase()}${secretNoun.slice(1)} (write-only)`;
  const secretPlaceholder = replacement
    ? 'Leave blank to keep the current value'
    : (secretExamplePlaceholder ?? 'Stored in Key Vault — never read back');
  if (shape === 'single') {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-secret`}>{secretLabel}</Label>
        <Input
          id={`${idPrefix}-secret`}
          type="password"
          value={draft.secret}
          onChange={(e) => onChange({ ...draft, secret: e.target.value })}
          placeholder={secretPlaceholder}
          autoComplete="off"
        />
      </div>
    );
  }
  const groupHint = replacement ? 'Leave all blank to keep the current set' : undefined;
  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-client-id`}>{clientIdLabel}</Label>
        <Input
          id={`${idPrefix}-client-id`}
          value={draft.clientId}
          onChange={(e) => onChange({ ...draft, clientId: e.target.value })}
          placeholder={groupHint ?? clientIdPlaceholder ?? 'OAuth client id'}
          className="font-mono"
          autoComplete="off"
        />
      </div>
      {shape === 'private_key_jwt' && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-certificate-id`}>Certificate ID</Label>
          <Input
            id={`${idPrefix}-certificate-id`}
            value={draft.certificateId}
            onChange={(e) => onChange({ ...draft, certificateId: e.target.value })}
            placeholder={groupHint ?? 'Certificate ID from the M2M mapping row'}
            className="font-mono"
            autoComplete="off"
          />
        </div>
      )}
      {shape === 'github_app' && (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-installation-id`}>Installation ID</Label>
          <Input
            id={`${idPrefix}-installation-id`}
            value={draft.certificateId}
            onChange={(e) => onChange({ ...draft, certificateId: e.target.value })}
            placeholder={groupHint ?? 'Numeric id at the end of the installation URL'}
            className="font-mono"
            autoComplete="off"
          />
          <p className="text-[11.5px] leading-snug text-muted-foreground">
            The installation this credential’s App is installed on. Greenlight mints this
            credential’s tokens against it, so a changed id takes effect on the next token mint.
          </p>
        </div>
      )}
      <div className="space-y-1.5">
        {shape === 'private_key_jwt' || shape === 'github_app' ? (
          <PrivateKeyEntry
            id={`${idPrefix}-secret`}
            label={secretLabel}
            value={draft.secret}
            onChange={(secret, generated) => onChange({ ...draft, secret, generated })}
            generated={draft.generated}
            allowGenerate={shape === 'private_key_jwt'}
            placeholder={secretPlaceholder}
          />
        ) : (
          <>
            <Label htmlFor={`${idPrefix}-secret`}>{secretLabel}</Label>
            <Input
              id={`${idPrefix}-secret`}
              type="password"
              value={draft.secret}
              onChange={(e) => onChange({ ...draft, secret: e.target.value })}
              placeholder={secretPlaceholder}
              autoComplete="off"
            />
          </>
        )}
        <p className="text-[11.5px] leading-snug text-muted-foreground">
          {shape === 'private_key_jwt'
            ? `The stored value is the id + certificate + ${secretNoun} set, entered together and never read back.`
            : shape === 'github_app'
              ? `The stored value is the App ID + installation ID + ${secretNoun} set, entered together and never read back.`
              : `The stored value is the id + ${secretNoun} pair, entered together and never read back.`}
        </p>
      </div>
    </>
  );
}
